import React, { useEffect, useRef } from 'react';

const ParticleNetwork = ({ isSectionActive = false }) => {
  const canvasRef = useRef(null);
  const activeRef = useRef(isSectionActive);
  const mouseInteractionRef = useRef({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
    radius: window.innerWidth > 768 ? 350 : 200,
    isActive: false,
    isManuallyMoved: false
  });

  // Sync prop to ref for the animation closure
  useEffect(() => {
    if (isSectionActive && !activeRef.current) {
        // Reset manual override on fresh entry so auto-start plays even after scroll-touch
        mouseInteractionRef.current.isManuallyMoved = false;
        mouseInteractionRef.current.x = window.innerWidth / 2;
        mouseInteractionRef.current.y = window.innerHeight / 2;
    }
    activeRef.current = isSectionActive;
  }, [isSectionActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false }); // Disable alpha for better performance with dark bg
    
    let animationFrameId;
    let particles = [];
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Force initial canvas dimensions!
    canvas.width = width;
    canvas.height = height;

    const mouse = mouseInteractionRef.current;

    const setDimensions = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      // Only resize and clear canvas if width changed or height changed significantly (> 150px)
      // This prevents flickering on mobile when the address bar toggles
      // or explicitly if canvas is small
      if (newWidth !== width || Math.abs(newHeight - height) > 150) {
        width = newWidth;
        height = newHeight;
        canvas.width = width;
        canvas.height = height;
        mouse.radius = width > 768 ? 350 : 200;
        
        // Re-initialize particles to fit new dimensions if width changed significantly
        if (particles.length > 0 && Math.abs(newWidth - width) > 100) {
          particles = [];
          const newParticleCount = Math.min(Math.floor((width * height) / 10000), 200);
          for (let i = 0; i < newParticleCount; i++) {
            particles.push(new Particle());
          }
        }
      }
    };

    // Responsive particle count for performance and aesthetics
    const particleCount = Math.min(Math.floor((width * height) / 10000), 200); 
    const connectionDistance = 180;

    let pulses = []; // Holds active data transfer pulses

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 0.5 + 0.1;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;

        this.baseSize = Math.random() * 1.5 + 0.5;
        this.size = this.baseSize;
        this.energy = 0; // Ranges from 0 (calm) to 1 (highly energized by mouse)
        
        this.connections = []; // Store currently close nodes
      }

      update() {
        // Continuous organic movement
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around edges smoothly
        if (this.x < -50) this.x = width + 50;
        if (this.x > width + 50) this.x = -50;
        if (this.y < -50) this.y = height + 50;
        if (this.y > height + 50) this.y = -50;

        // Logic for activating background effect on scroll entry
        let effectiveActive = mouse.isActive;
        let targetX = mouse.x;
        let targetY = mouse.y;

        // Use the ref to get the latest prop value inside the closure
        if (!effectiveActive && activeRef.current && !mouse.isManuallyMoved) {
          effectiveActive = true;
          targetX = width / 2;
          targetY = height / 2;
        }

        if (effectiveActive) {
          const dx = targetX - this.x;
          const dy = targetY - this.y;
          const distToMouse = Math.sqrt(dx * dx + dy * dy);

          if (distToMouse < mouse.radius) {
            const targetEnergy = Math.pow(1 - (distToMouse / mouse.radius), 1.5); 
            this.energy += (targetEnergy - this.energy) * 0.1;

            const force = (mouse.radius - distToMouse) / mouse.radius;
            const pullStrength = 0.03;
            const swirlStrength = 0.08;
            
            this.vx += (dx / distToMouse) * force * pullStrength;
            this.vy += (dy / distToMouse) * force * pullStrength;
            this.vx += (dy / distToMouse) * force * swirlStrength;
            this.vy -= (dx / distToMouse) * force * swirlStrength;
          } else {
            this.energy += (0 - this.energy) * 0.05;
          }
        } else {
          this.energy += (0 - this.energy) * 0.05;
        }

        // Friction ensures they don't accelerate infinitely
        this.vx *= 0.98;
        this.vy *= 0.98;

        // Maintain minimum 'drifting' speed
        const currentSpeed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (currentSpeed < 0.2 && this.energy < 0.1) {
             const angle = Math.atan2(this.vy, this.vx) || (Math.random() * Math.PI * 2);
             this.vx += Math.cos(angle) * 0.02;
             this.vy += Math.sin(angle) * 0.02;
        }

        // Size pulses with energy
        this.size = this.baseSize + (this.energy * 3.5);

        // Periodically fire a data pulse if energized
        if (this.energy > 0.3 && Math.random() < 0.03 && this.connections.length > 0) {
            const target = this.connections[Math.floor(Math.random() * this.connections.length)];
            pulses.push({
                x: this.x,
                y: this.y,
                target: target,
                progress: 0,
                speed: 0.04 + Math.random() * 0.04 // vary speed slightly
            });
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        
        // Base color is dim grey. Energy introduces FOZZIL cyan/white.
        // Base: rgb(50, 60, 80) -> Energized: rgb(200, 240, 255)
        const r = Math.floor(50 + this.energy * 150);
        const g = Math.floor(60 + this.energy * 180);
        const b = Math.floor(80 + this.energy * 175);
        
        // Dim base alpha, bursting to 1.0 when energized
        const a = 0.2 + this.energy * 0.8;
        
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
        
        // Powerful glow effect when energized
        if (this.energy > 0.3) {
            ctx.shadowBlur = this.energy * 20;
            ctx.shadowColor = 'rgba(96, 165, 250, 0.8)'; // theme blue glow
        } else {
            ctx.shadowBlur = 0;
        }
        
        ctx.fill();
        ctx.shadowBlur = 0; // reset
      }
    }

    // Initialize network
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const drawConnections = () => {
      // Clear connections for the pulse logic
      for(let p of particles) p.connections = [];

      // Scale connection distance dynamically based on screen size so connections actually form
      const scaledConnectionDistance = Math.min(width, height) > 600 ? 180 : 120;

      // Create a web of connections
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];

        // 1. Centralized "Nexus" Connections
        let effectiveActive = mouse.isActive;
        let targetX = mouse.x;
        let targetY = mouse.y;

        if (!effectiveActive && activeRef.current && !mouse.isManuallyMoved) {
          effectiveActive = true;
          targetX = width / 2;
          targetY = height / 2;
        }

        if (effectiveActive) {
          const dxMouse = targetX - p1.x;
          const dyMouse = targetY - p1.y;
          const distMouse = Math.hypot(dxMouse, dyMouse);
          
          if (distMouse < mouse.radius * 0.7) {
             const mouseEdgeOpacity = Math.pow(1 - distMouse / (mouse.radius * 0.7), 2) * 0.9;
             ctx.beginPath();
             ctx.moveTo(p1.x, p1.y);
             ctx.lineTo(targetX, targetY);
             
             const grad = ctx.createLinearGradient(targetX, targetY, p1.x, p1.y);
             grad.addColorStop(0, `rgba(59, 130, 246, ${mouseEdgeOpacity})`);
             grad.addColorStop(0.5, `rgba(147, 197, 253, ${mouseEdgeOpacity * 0.5})`);
             grad.addColorStop(1, `rgba(147, 197, 253, 0)`);
             
             ctx.strokeStyle = grad;
             ctx.lineWidth = 1 + mouseEdgeOpacity * 1.5;
             ctx.stroke();
          }
        }

        // 2. Synaptic Connections between Particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.hypot(dx, dy);

          if (distance < scaledConnectionDistance) {
            // Register connection for pulses
            p1.connections.push(p2);
            p2.connections.push(p1);

            // Distance opacity (linear fallback)
            let baseOpacity = 1 - (distance / scaledConnectionDistance);
            
            // If both nodes are energized by the mouse, the connection line "lights up" brightly
            const combinedEnergy = (p1.energy + p2.energy) / 2;
            
            // Non-energized nodes have extremely faint connections, creating a dark, dormant feel
            // Energized nodes suddenly connect vividly.
            const finalOpacity = (0.05 * baseOpacity) + (combinedEnergy * 0.85);

            if (finalOpacity > 0.02) {
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                
                if (combinedEnergy > 0.4) {
                    ctx.strokeStyle = `rgba(147, 197, 253, ${finalOpacity})`; // High energy cyan/blue
                    ctx.lineWidth = 0.5 + combinedEnergy * 1.5; // Thicker lines for active synapses
                } else {
                    ctx.strokeStyle = `rgba(71, 85, 105, ${finalOpacity * 2})`; // Low energy slate grey
                    ctx.lineWidth = 0.5;
                }
                
                ctx.stroke();
            }
          }
        }
      }
    };

    const drawMouseGlow = () => {
        let effectiveActive = mouse.isActive;
        let targetX = mouse.x;
        let targetY = mouse.y;

        if (!effectiveActive && activeRef.current && !mouse.isManuallyMoved) {
          effectiveActive = true;
          targetX = width / 2;
          targetY = height / 2;
        }

        if (!effectiveActive) return;

        ctx.beginPath();
        const grad = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, mouse.radius * 0.8);
        grad.addColorStop(0, 'rgba(37, 99, 235, 0.08)'); 
        grad.addColorStop(0.4, 'rgba(59, 130, 246, 0.03)');
        grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = grad;
        ctx.arc(targetX, targetY, mouse.radius * 0.8, 0, Math.PI * 2);
        ctx.fill();
    };

    const drawPulses = () => {
        for(let i = pulses.length - 1; i >= 0; i--) {
            let p = pulses[i];
            p.progress += p.speed;
            
            if (p.progress >= 1) {
                // Pulse arrived, energize target slightly to create a chain reaction
                p.target.energy = Math.min(p.target.energy + 0.3, 1);
                pulses.splice(i, 1);
                continue;
            }
            
            const currX = p.x + (p.target.x - p.x) * p.progress;
            const currY = p.y + (p.target.y - p.y) * p.progress;
            
            ctx.beginPath();
            ctx.arc(currX, currY, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'; // Bright white core
            ctx.shadowBlur = 10;
            ctx.shadowColor = 'rgba(96, 165, 250, 1)'; // Blue glow
            ctx.fill();
            ctx.shadowBlur = 0;
        }
    };

    const animate = () => {
      // Ultra-smooth trailing effect
      // Lower alpha = longer trails. 0.3 strikes a balance between motion blur and crispness.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'; 
      ctx.fillRect(0, 0, width, height);

      drawMouseGlow();

      particles.forEach(p => p.update());
      drawConnections();
      drawPulses();
      // Draw nodes last so they appear above connecting lines
      particles.forEach(p => p.draw());

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Use a slightly throttled resize for better performance
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setDimensions, 100);
    };

    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
      mouse.isActive = true;
      mouse.isManuallyMoved = true;
    };

    const handleMouseLeave = () => {
      mouse.isActive = false;
      mouse.x = -1000;
      mouse.y = -1000;
    };

    const handleTouchStart = (event) => {
      if (event.touches.length > 0) {
        const rect = canvas.getBoundingClientRect();
        mouse.x = event.touches[0].clientX - rect.left;
        mouse.y = event.touches[0].clientY - rect.top;
        mouse.isActive = true;
        mouse.isManuallyMoved = true;
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length > 0) {
        const rect = canvas.getBoundingClientRect();
        mouse.x = event.touches[0].clientX - rect.left;
        mouse.y = event.touches[0].clientY - rect.top;
        mouse.isActive = true;
      }
    };

    const handleTouchEnd = () => {
      mouse.isActive = false;
      mouse.x = -1000;
      mouse.y = -1000;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 w-full h-full block"
      style={{ 
        background: '#000', 
        cursor: 'crosshair', // Enhanced futuristic UX indicator
      }} 
    />
  );
};

export default ParticleNetwork;
